import type {RequestOptions, RequestConfig} from '@@/plugin-request/request';
import {history} from '@umijs/max';
import {message} from 'antd';
import {stringify} from 'querystring';

// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;
  data: any;
  msg: string;
}

export const buildAuthorizationValue = (token: string | null | undefined): string | undefined => {
  const normalizedToken = token?.trim();
  if (!normalizedToken) {
    return undefined;
  }
  if (normalizedToken.startsWith('Bearer ')) {
    return normalizedToken;
  }
  return `Bearer ${normalizedToken}`;
};

export const getAuthorizationHeaderValue = () => {
  return buildAuthorizationValue(getToken());
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const requestConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const {code, data, msg} = res as unknown as ResponseStructure;
      if (code !== 200) {
        const error: any = new Error(msg);
        error.name = 'BizError';
        error.info = {code, msg, data};
        throw error;
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo && errorInfo.msg) {
          message.error(errorInfo.msg);
        }
      } else if (error.response?.status === 401) {
        localStorage.removeItem('xtoken');
        if (window.location.pathname !== '/user/login') {
          const {pathname, search} = window.location;
          history.replace({
            pathname: '/user/login',
            search: stringify({
              redirect: pathname + search,
            }),
          });
        }
      } else if (error.response?.status) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(error.message);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    // 拦截请求配置，进行个性化处理。
    (config: RequestOptions) => {
      const nextConfig = {...config};
      const headers = {...(nextConfig.headers ?? {})};

      if (headers['Client-Region'] !== 'Null') {
        nextConfig.baseURL = sessionStorage.getItem('baseURL') ?? process.env.UMI_APP_BASE_URL;
      }
      delete headers['Client-Region'];

      const token = getToken();
      const authorization = buildAuthorizationValue(token);
      if (authorization) {
        headers.Authorization = authorization;
      }

      nextConfig.headers = headers;
      return nextConfig;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const {code, data} = response as unknown as ResponseStructure;
      if (code !== 200) {
        data.success = false;
      }
      return response;
    },
  ],
};

export const getBaseURL = () => {
  return sessionStorage.getItem('baseURL') ?? process.env.UMI_APP_BASE_URL ?? '';
};

export const getToken = () => {
  return localStorage.getItem('xtoken') ?? '';
};
